import React from "react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";

function formatPlacementCount(count) {
  return `${count} ${count === 1 ? "Einsatzort" : "Einsatzorte"}`;
}

function formatFormKindLabel(formKind) {
  const labels = {
    general_contact: "Allgemeiner Kontakt",
    demo_request: "Demo-Anfrage",
    strategy_call: "Strategiegespräch",
    whitepaper_download: "Whitepaper-Download",
  };
  return labels[formKind] || formKind || "Ohne Typ";
}

function formatCompactDate(value) {
  if (!value) {
    return "Unbekannt";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Unbekannt";
  }
  return date.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function PublicFormsList({
  forms,
  selectedFormId,
  onSelect,
  createName,
  onCreateNameChange,
  createKind,
  onCreateKindChange,
  onCreate,
  createDisabled,
  createDisabledReason,
}) {
  return (
    <Card className="border-border/70 bg-card/80 shadow-sm" data-testid="forms-overview-list">
      <CardHeader>
        <CardTitle>Formulare</CardTitle>
        <CardDescription>
          Verwalten Sie mehrere öffentliche Formulare und wählen Sie ein Formular zur Bearbeitung aus.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          <Input
            value={createName}
            onChange={(event) => onCreateNameChange(event.target.value)}
            placeholder="Neues Formular, z. B. Whitepaper Techniker"
            data-testid="public-forms-name-input"
          />
          <div
            data-testid="public-forms-create-row"
            className="grid min-w-0 gap-3 sm:grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto] md:items-start"
          >
            <div className="min-w-0">
              <select
                data-testid="public-forms-kind-select"
                className="block h-10 min-w-0 w-full rounded-md border border-input bg-background px-3 text-sm shadow-none outline-none focus-visible:border-brand/60 focus-visible:ring-2 focus-visible:ring-brand/35"
                value={createKind}
                onChange={(event) => onCreateKindChange(event.target.value)}
              >
                <option value="general_contact">Allgemeiner Kontakt</option>
                <option value="demo_request">Demo-Anfrage</option>
                <option value="strategy_call">Strategiegespräch</option>
                <option value="whitepaper_download">Whitepaper-Download</option>
              </select>
            </div>
            <Button
              data-testid="public-forms-create-button"
              disabled={createDisabled}
              onClick={onCreate}
              className="w-full min-w-0 disabled:cursor-not-allowed disabled:shadow-none md:w-auto md:flex-none"
            >
              Neues Formular
            </Button>
          </div>
        </div>
        {createDisabledReason ? (
          <div className="text-xs text-muted-foreground">{createDisabledReason}</div>
        ) : null}

        <div
          data-testid="public-forms-grid"
          className="grid gap-3 sm:grid-cols-1 md:grid-cols-2 xl:grid-cols-3"
        >
          {forms.map((form) => {
            const isSelected = form.id === selectedFormId;
            return (
              <div
                key={form.id}
                onClick={() => onSelect(form.id)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelect(form.id);
                  }
                }}
                role="button"
                tabIndex={0}
                className={`w-full rounded-xl border px-4 py-3 text-left transition-colors ${
                  isSelected
                    ? "border-brand/40 bg-brand/5 shadow-sm"
                    : "border-border/70 bg-card/60 hover:border-border hover:bg-card/80"
                }`}
              >
                <div className="flex h-full flex-col gap-3">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 space-y-2">
                      <div className="font-medium">{form.name || "Unbenanntes Formular"}</div>
                      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <span className="rounded-full border border-border/70 bg-background/70 px-2 py-1">
                          {formatFormKindLabel(form.form_kind)}
                        </span>
                        <span className="rounded-full border border-border/70 bg-background/70 px-2 py-1">
                          {form.is_active ? "Aktiv" : "Inaktiv"}
                        </span>
                        <span className="rounded-full border border-border/70 bg-background/70 px-2 py-1">
                          {formatPlacementCount(form.placement_count || 0)}
                        </span>
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Geändert: {formatCompactDate(form.updated_at)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
